"use client";

import { useState, type ReactNode } from "react";
import Link from "next/link";
import { ArrowRight, Loader2, X } from "lucide-react";
import { differenceInCalendarDays, format } from "date-fns";
import { srLatn } from "date-fns/locale";
import { api } from "~/trpc/react";
import { useBookingResume } from "~/hooks/use-booking-resume";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { buttonVariants } from "~/components/ui/button";
import { capturePostHogEvent } from "~/lib/posthog/client";

export function HomepageBookingResume({ children }: { children: ReactNode }) {
  const { cartItem, resumeUrl, needsNewTime, now, clearBookingCart } =
    useBookingResume();
  const [isClearing, setIsClearing] = useState(false);
  const [clearError, setClearError] = useState(false);

  async function dismiss() {
    setIsClearing(true);
    setClearError(false);
    try {
      await clearBookingCart();
    } catch {
      setClearError(true);
    } finally {
      setIsClearing(false);
    }
  }

  if (!cartItem || !resumeUrl || !now) return children;
  const date = new Date(cartItem.selectedDateTime);
  const daysAway = differenceInCalendarDays(date, now);
  const day =
    daysAway === 0
      ? "Danas"
      : daysAway === 1
        ? "Sutra"
        : format(date, "d. MMM", { locale: srLatn });

  return (
    <div
      className="mx-auto mt-8 max-w-sm text-left"
      role="region"
      aria-label="Nedovršeno zakazivanje"
    >
      <div className="relative flex items-center gap-4 pr-9">
        <Avatar className="h-16 w-16 shrink-0 rounded-lg">
          <AvatarImage src={cartItem.therapistImage} alt="" />
          <AvatarFallback className="rounded-lg">
            {cartItem.therapistName?.slice(0, 1)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <h2 className="text-lg leading-tight font-bold break-words text-white">
            {cartItem.therapistName ?? "Terapeut"}
          </h2>
          <p className="text-editorial-paper-contrast/70 mt-1 text-xs">
            Započeto zakazivanje
          </p>
          <p className="text-editorial-paper-contrast/90 mt-1 min-h-5 text-sm leading-snug">
            {needsNewTime ? (
              <FirstAvailableSlot
                therapistSlug={cartItem.therapistSlug}
                sessionType={
                  cartItem.therapyType === "COUPLES" ? "COUPLES" : "INDIVIDUAL"
                }
              />
            ) : (
              <time dateTime={cartItem.selectedDateTime}>
                {day} u {format(date, "HH:mm")}
              </time>
            )}
          </p>
        </div>
        <button
          type="button"
          disabled={isClearing}
          onClick={dismiss}
          aria-label="Ukloni podsetnik"
          title="Ukloni podsetnik"
          className="text-editorial-paper-contrast/70 absolute -top-2 -right-2 flex h-11 w-11 items-center justify-center rounded-full transition-colors hover:bg-editorial-paper-contrast/10 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-50"
        >
          {isClearing ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <X className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </div>
      <Link
        href={resumeUrl}
        className={buttonVariants({
          variant: "cta",
          size: "lg",
          className:
            "mt-5 h-12 w-full justify-between px-6 text-base font-semibold",
        })}
        onClick={() =>
          capturePostHogEvent("homepage_cta_clicked", {
            placement: "hero",
            destination: "booking_resume",
          })
        }
      >
        {needsNewTime ? "Izaberi novi termin" : "Nastavi zakazivanje"}
        <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
      </Link>
      <div className="text-editorial-paper-contrast/80 mt-1 flex items-center justify-center text-sm">
        <Link
          href="/terapeuti"
          className="inline-flex min-h-11 items-center rounded-sm underline-offset-4 hover:text-white hover:underline focus-visible:outline-2"
        >
          Izaberi drugog terapeuta
        </Link>
      </div>
      {clearError && (
        <p role="alert" className="mt-1 text-sm text-white">
          Podsetnik nije uklonjen. Pokušaj ponovo.
        </p>
      )}
    </div>
  );
}

function FirstAvailableSlot({
  therapistSlug,
  sessionType,
}: {
  therapistSlug: string;
  sessionType: "INDIVIDUAL" | "COUPLES";
}) {
  const availability = api.calendar.getAvailabilitySummary.useQuery(
    { therapistSlug, sessionType, includeWeeklySpots: false },
    { staleTime: 60_000, refetchInterval: 60_000 },
  );
  if (availability.isPending)
    return (
      <span
        className="inline-block h-4 w-full max-w-48 animate-pulse rounded bg-editorial-paper-contrast/15"
        aria-label="Provera slobodnih termina"
      />
    );
  if (availability.isError) return <span>Proveri slobodne termine</span>;
  const firstSlot = availability.data?.firstAvailableDay;
  if (!firstSlot) return <span>Trenutno nema slobodnih termina</span>;
  const date = new Date(firstSlot);
  const daysAway = differenceInCalendarDays(date, new Date());
  const day =
    daysAway === 0
      ? "danas"
      : daysAway === 1
        ? "sutra"
        : format(date, "d. MMM", { locale: srLatn });
  return (
    <span>
      Prvi slobodan termin:{" "}
      <time dateTime={date.toISOString()}>
        {day} u {format(date, "HH:mm")}
      </time>
    </span>
  );
}
